import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, Building2, Package, MessageSquare, Edit, Zap, Copy, Check, Database, Undo2, AlertTriangle, ShieldAlert, History, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import PublicPageEditor from '../components/PublicPageEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const [insurers, setInsurers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insurerDialog, setInsurerDialog] = useState(false);
  const [productDialog, setProductDialog] = useState(false);
  const [insurerForm, setInsurerForm] = useState({ name: '', category: 'Life' });
  const [productForm, setProductForm] = useState({ name: '', insurer: '', product_type: 'Term', description: '' });

  // Message templates
  const [templates, setTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', category: 'custom', channel: 'both', body: '' });

  // Twilio settings
  const [twilio, setTwilio] = useState(null);
  const [twilioForm, setTwilioForm] = useState({ account_sid: '', auth_token: '', sms_from: '', whatsapp_from: '' });
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);

  // Data management
  const [imports, setImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [dataSummary, setDataSummary] = useState({ counts: {}, total: 0 });
  const [rollbackBusyId, setRollbackBusyId] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  // Commission rates
  const [commissionRates, setCommissionRates] = useState([]);
  const [editingRateId, setEditingRateId] = useState('');
  const [editingRateValue, setEditingRateValue] = useState('');
  const [addRateOpen, setAddRateOpen] = useState(false);
  const [rateForm, setRateForm] = useState({ product_type: 'Health', category: 'general_insurance', insurer: '', product_name: '', policy_stage: 'Both', rate_percent: '' });
  const [availableInsurers, setAvailableInsurers] = useState([]);

  // Commission annexures (rate-schedule PDFs)
  const [annexures, setAnnexures] = useState([]);
  const [annexureForm, setAnnexureForm] = useState({ insurer: '', product_type: '', label: '', file: null });
  const [annexureUploading, setAnnexureUploading] = useState(false);

  const loadAnnexures = async () => {
    try {
      const { data } = await axios.get(`${API}/commission-annexures`, { withCredentials: true });
      setAnnexures(data || []);
    } catch (e) { /* silent — panel just shows empty */ }
  };
  const uploadAnnexure = async () => {
    if (!annexureForm.file || !annexureForm.insurer) { toast.error('Insurer and file are required'); return; }
    setAnnexureUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', annexureForm.file);
      fd.append('insurer', annexureForm.insurer);
      if (annexureForm.product_type) fd.append('product_type', annexureForm.product_type);
      if (annexureForm.label) fd.append('label', annexureForm.label);
      await axios.post(`${API}/commission-annexures`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Annexure uploaded');
      setAnnexureForm({ insurer: '', product_type: '', label: '', file: null });
      loadAnnexures();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setAnnexureUploading(false); }
  };
  const deleteAnnexure = async (id) => {
    if (!window.confirm('Delete this annexure? The file will be permanently removed.')) return;
    try {
      await axios.delete(`${API}/commission-annexures/${id}`, { withCredentials: true });
      toast.success('Annexure deleted');
      loadAnnexures();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const downloadAnnexure = async (a) => {
    try {
      const res = await axios.get(`${API}/commission-annexures/${a.annexure_id}/download`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url; link.download = a.filename || 'annexure';
      document.body.appendChild(link); link.click();
      link.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const loadCommissionRates = async () => {
    try {
      const [{ data }, ins] = await Promise.all([
        axios.get(`${API}/commission-rates`, { withCredentials: true }),
        axios.get(`${API}/insurers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCommissionRates(data);
      setAvailableInsurers(ins.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load commission rates');
    }
  };
  const saveRate = async (r) => {
    try {
      await axios.put(`${API}/commission-rates/${r.rate_id}`, { rate_percent: parseFloat(editingRateValue) || 0 }, { withCredentials: true });
      toast.success(`${r.product_type} rate saved`);
      setEditingRateId(''); setEditingRateValue('');
      loadCommissionRates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const deleteRate = async (r) => {
    if (!window.confirm(`Delete this ${r.product_type} rate override?`)) return;
    try {
      await axios.delete(`${API}/commission-rates/${r.rate_id}`, { withCredentials: true });
      toast.success('Rate deleted');
      loadCommissionRates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const addRate = async (e) => {
    e.preventDefault();
    if (!rateForm.product_type || rateForm.rate_percent === '') { toast.error('Product type and rate % are required'); return; }
    try {
      await axios.post(`${API}/commission-rates`, {
        product_type: rateForm.product_type,
        category: rateForm.category,
        insurer: rateForm.insurer || null,
        product_name: rateForm.product_name || null,
        policy_stage: rateForm.policy_stage,
        rate_percent: parseFloat(rateForm.rate_percent),
      }, { withCredentials: true });
      toast.success('Override added');
      setAddRateOpen(false);
      setRateForm({ product_type: 'Health', category: 'general_insurance', insurer: '', product_name: '', policy_stage: 'Both', rate_percent: '' });
      loadCommissionRates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add rate'); }
  };

  useEffect(() => { loadData(); }, []);

  // Load commission annexures alongside rates so the Settings > Commissions
  // tab renders both panels on first mount.
  useEffect(() => { loadAnnexures(); }, []);

  const loadDataManagement = async () => {
    setImportsLoading(true);
    try {
      const [imp, sum, aud] = await Promise.all([
        axios.get(`${API}/imports`, { withCredentials: true }),
        axios.get(`${API}/data-management/summary`, { withCredentials: true }),
        axios.get(`${API}/data-management/audit-log?limit=30`, { withCredentials: true }),
      ]);
      setImports(imp.data || []);
      setDataSummary(sum.data || { counts: {}, total: 0 });
      setAuditLog(aud.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load data management');
    } finally { setImportsLoading(false); }
  };

  const rollbackBatch = async (batch) => {
    if (batch.rolled_back_at) return;
    if (!window.confirm(`Rollback "${batch.filename}" — this will permanently delete ${batch.inserted} records inserted in this batch. Continue?`)) return;
    setRollbackBusyId(batch.batch_id);
    try {
      const r = await axios.delete(`${API}/imports/${batch.batch_id}`, { withCredentials: true });
      toast.success(`Rolled back ${r.data.deleted_total} records`);
      loadDataManagement();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rollback failed');
    } finally { setRollbackBusyId(null); }
  };

  const purgeAll = async () => {
    if (purgeConfirm !== 'DELETE ALL') {
      toast.error('Type exactly "DELETE ALL" to confirm');
      return;
    }
    setPurgeBusy(true);
    try {
      const r = await axios.post(`${API}/data-management/purge`, { confirmation: purgeConfirm }, { withCredentials: true });
      toast.success(`${r.data.message || 'All data cleared'} — redirecting…`);
      setPurgeOpen(false);
      setPurgeConfirm('');
      // Take the user to the friendly empty-state dashboard instead of leaving them on
      // an empty Settings view that still shows their old (stale) purge summary.
      setTimeout(() => { window.location.href = '/dashboard'; }, 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally { setPurgeBusy(false); }
  };

  const loadData = async () => {
    try {
      const [i, p, t, tw] = await Promise.all([
        axios.get(`${API}/settings/insurers`, { withCredentials: true }),
        axios.get(`${API}/settings/products`, { withCredentials: true }),
        axios.get(`${API}/message-templates`, { withCredentials: true }),
        axios.get(`${API}/settings/twilio`, { withCredentials: true })
      ]);
      setInsurers(i.data);
      setProducts(p.data);
      setTemplates(t.data);
      setTwilio(tw.data);
    } catch (error) { toast.error('Failed to load settings'); } finally { setLoading(false); }
  };

  const saveTwilio = async (e) => {
    e.preventDefault();
    setTwilioSaving(true);
    try {
      // Only send fields the user actually filled — empty means "don't touch"
      const payload = {};
      Object.keys(twilioForm).forEach(k => { if (twilioForm[k]) payload[k] = twilioForm[k]; });
      if (Object.keys(payload).length === 0) {
        toast.error('Please fill at least one field to save');
        setTwilioSaving(false);
        return;
      }
      await axios.put(`${API}/settings/twilio`, payload, { withCredentials: true });
      toast.success('Twilio credentials saved. Delivery is now live.');
      setTwilioForm({ account_sid: '', auth_token: '', sms_from: '', whatsapp_from: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setTwilioSaving(false); }
  };

  const copyWebhook = async () => {
    if (!twilio?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(twilio.webhook_url);
      setWebhookCopied(true);
      toast.success('Webhook URL copied to clipboard');
      setTimeout(() => setWebhookCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy — please copy manually');
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/message-templates/${editingTemplate.template_id}`, templateForm, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/message-templates`, templateForm, { withCredentials: true });
        toast.success('Template created');
      }
      setTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', category: 'custom', channel: 'both', body: '' });
      loadData();
    } catch (e) { toast.error('Failed to save template'); }
  };

  const editTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, category: t.category, channel: t.channel, body: t.body });
    setTemplateDialog(true);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await axios.delete(`${API}/message-templates/${id}`, { withCredentials: true }); toast.success('Deleted'); loadData(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const addInsurer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/settings/insurers`, insurerForm, { withCredentials: true });
      toast.success('Insurer added');
      setInsurerDialog(false);
      setInsurerForm({ name: '', category: 'Life' });
      loadData();
    } catch (error) { toast.error('Failed to add insurer'); }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/settings/products`, productForm, { withCredentials: true });
      toast.success('Product added');
      setProductDialog(false);
      setProductForm({ name: '', insurer: '', product_type: 'Term', description: '' });
      loadData();
    } catch (error) { toast.error('Failed to add product'); }
  };

  const deleteInsurer = async (id) => {
    if (!window.confirm('Delete this insurer?')) return;
    try { await axios.delete(`${API}/settings/insurers/${id}`, { withCredentials: true }); toast.success('Deleted'); loadData(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete(`${API}/settings/products/${id}`, { withCredentials: true }); toast.success('Deleted'); loadData(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-slate-600 mt-1">Manage your account and master data</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="insurers" data-testid="tab-insurers">Insurers</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Message Templates</TabsTrigger>
          <TabsTrigger value="twilio" data-testid="tab-twilio">Twilio</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions" onClick={loadCommissionRates}>Commissions</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data" onClick={loadDataManagement}>Data</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Profile Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                {user?.profile_photo ? (
                  <img src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`} alt={user?.name} className="h-20 w-20 rounded-full object-cover border border-slate-200" data-testid="profile-photo-preview" />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-blue-800 text-white flex items-center justify-center text-xl font-bold" data-testid="profile-photo-placeholder">{(user?.name || '?').slice(0, 2).toUpperCase()}</div>
                )}
                <div className="flex-1 space-y-1">
                  <Label className="text-slate-600 text-xs">Profile Photo (Item 4) — shown in the topbar, on your Dashboard greeting and on your public agent page</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        if (file.size > 3 * 1024 * 1024) { toast.error('Photo must be under 3 MB'); return; }
                        try {
                          const fd = new FormData(); fd.append('file', file);
                          await axios.post(`${API}/profile/photo`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                          toast.success('Photo uploaded — reload to see everywhere');
                          setTimeout(() => window.location.reload(), 800);
                        } catch (ex) { toast.error(ex.response?.data?.detail || 'Upload failed'); }
                      }}
                      data-testid="profile-photo-input"
                      className="text-xs"
                    />
                    <span className="text-[10px] text-slate-500">JPG / PNG / WebP · Max 3 MB</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 pt-2">
                <div><Label className="text-slate-600">Name</Label><p className="text-lg font-semibold">{user?.name}</p></div>
                <div><Label className="text-slate-600">Email</Label><p className="text-lg font-semibold">{user?.email}</p></div>
                <div><Label className="text-slate-600">Phone</Label><p className="text-lg font-semibold">{user?.phone || 'Not set'}</p></div>
                <div><Label className="text-slate-600">Role</Label><p className="text-lg font-semibold capitalize">{user?.role}</p></div>
              </div>
            </CardContent>
          </Card>

          {/* Item 5 — Personal Agent Landing Page */}
          <Card className="border-slate-200" data-testid="public-page-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>My Public Agent Page</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Shareable URL your prospects can visit — includes your bio, contact buttons and a lead-capture form that drops straight into your Leads inbox.</p>
            </CardHeader>
            <CardContent>
              <PublicPageEditor />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insurers" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}><Building2 className="inline h-5 w-5 mr-2" />Insurer Master List</CardTitle>
              <Dialog open={insurerDialog} onOpenChange={setInsurerDialog}>
                <DialogTrigger asChild><Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-insurer-button"><Plus className="h-4 w-4 mr-2" />Add Insurer</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Insurer</DialogTitle></DialogHeader>
                  <form onSubmit={addInsurer} className="space-y-4">
                    <div><Label>Name *</Label><Input value={insurerForm.name} onChange={(e) => setInsurerForm({...insurerForm, name: e.target.value})} required data-testid="insurer-name-input" /></div>
                    <div><Label>Category</Label>
                      <Select value={insurerForm.category} onValueChange={(v) => setInsurerForm({...insurerForm, category: v})}>
                        <SelectTrigger data-testid="insurer-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Life">Life</SelectItem>
                          <SelectItem value="General">General</SelectItem>
                          <SelectItem value="Both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900" data-testid="insurer-submit-button">Add</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {insurers.length === 0 ? <p className="text-center text-slate-500 py-8">No insurers added yet</p> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {insurers.map(i => (
                      <TableRow key={i.insurer_id}>
                        <TableCell className="font-medium">{i.name}</TableCell>
                        <TableCell>{i.category}</TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => deleteInsurer(i.insurer_id)}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}><Package className="inline h-5 w-5 mr-2" />Product Master List</CardTitle>
              <Dialog open={productDialog} onOpenChange={setProductDialog}>
                <DialogTrigger asChild><Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-product-button"><Plus className="h-4 w-4 mr-2" />Add Product</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Product</DialogTitle></DialogHeader>
                  <form onSubmit={addProduct} className="space-y-4">
                    <div><Label>Product Name *</Label><Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} required data-testid="product-name-input" /></div>
                    <div><Label>Insurer *</Label><Input value={productForm.insurer} onChange={(e) => setProductForm({...productForm, insurer: e.target.value})} required data-testid="product-insurer-input" /></div>
                    <div><Label>Product Type</Label>
                      <Select value={productForm.product_type} onValueChange={(v) => setProductForm({...productForm, product_type: v})}>
                        <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Term">Term</SelectItem>
                          <SelectItem value="Endowment">Endowment</SelectItem>
                          <SelectItem value="ULIP">ULIP</SelectItem>
                          <SelectItem value="Health">Health</SelectItem>
                          <SelectItem value="Motor">Motor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900" data-testid="product-submit-button">Add</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? <p className="text-center text-slate-500 py-8">No products added yet</p> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Insurer</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {products.map(p => (
                      <TableRow key={p.product_id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>{p.insurer}</TableCell>
                        <TableCell>{p.product_type}</TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => deleteProduct(p.product_id)}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}><MessageSquare className="inline h-5 w-5 mr-2" />Message Templates</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Reusable SMS/WhatsApp templates with auto-filled placeholders</p>
              </div>
              <Dialog open={templateDialog} onOpenChange={(o) => { setTemplateDialog(o); if (!o) { setEditingTemplate(null); setTemplateForm({ name: '', category: 'custom', channel: 'both', body: '' }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-template-button"><Plus className="h-4 w-4 mr-2" />New Template</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingTemplate ? 'Edit' : 'New'} Message Template</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={saveTemplate} className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-3"><Label>Name *</Label><Input value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} required data-testid="template-name-input" /></div>
                      <div><Label>Category</Label>
                        <Select value={templateForm.category} onValueChange={(v) => setTemplateForm({...templateForm, category: v})}>
                          <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="renewal">Renewal</SelectItem>
                            <SelectItem value="greeting">Greeting</SelectItem>
                            <SelectItem value="gratitude">Gratitude</SelectItem>
                            <SelectItem value="followup">Follow-up</SelectItem>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Channel</Label>
                        <Select value={templateForm.channel} onValueChange={(v) => setTemplateForm({...templateForm, channel: v})}>
                          <SelectTrigger data-testid="template-channel-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both">Both (SMS + WhatsApp)</SelectItem>
                            <SelectItem value="sms">SMS only</SelectItem>
                            <SelectItem value="whatsapp">WhatsApp only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Body *</Label>
                      <Textarea value={templateForm.body} onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})} rows={5} required data-testid="template-body-input" />
                      <p className="text-xs text-slate-500 mt-2">
                        Placeholders you can use: <code className="text-blue-800">{'{client_name}'}</code>, <code className="text-blue-800">{'{agent_name}'}</code>, <code className="text-blue-800">{'{policy_number}'}</code>, <code className="text-blue-800">{'{expiry_date}'}</code>, <code className="text-blue-800">{'{days_remaining}'}</code>, <code className="text-blue-800">{'{sum_assured}'}</code>, <code className="text-blue-800">{'{premium_amount}'}</code>
                      </p>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setTemplateDialog(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="template-submit-button">{editingTemplate ? 'Update' : 'Create'} Template</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? <p className="text-center text-slate-500 py-8">No templates yet</p> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Channel</TableHead><TableHead>Body Preview</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {templates.map(t => (
                      <TableRow key={t.template_id} data-testid={`template-row-${t.template_id}`}>
                        <TableCell className="font-medium">
                          {t.name}
                          {t.is_default && <Badge variant="outline" className="ml-2 text-xs">default</Badge>}
                        </TableCell>
                        <TableCell><Badge className="bg-slate-100 text-slate-800 capitalize">{t.category}</Badge></TableCell>
                        <TableCell><span className="text-xs uppercase tracking-wider text-slate-500">{t.channel}</span></TableCell>
                        <TableCell className="max-w-md truncate text-sm text-slate-600">{t.body}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => editTemplate(t)} data-testid={`edit-template-${t.template_id}`}><Edit className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteTemplate(t.template_id)} data-testid={`delete-template-${t.template_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="twilio" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}><Zap className="inline h-5 w-5 mr-2" />Twilio Credentials</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  {twilio?.configured
                    ? <span className="text-green-700 font-medium">✓ Twilio is connected · SMS & WhatsApp are live</span>
                    : <span className="text-amber-700">Not yet connected · SMS/WhatsApp are placeholders and messages return HTTP 503</span>}
                </p>
              </CardHeader>
              <CardContent>
                {twilio?.configured && (
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1 text-sm">
                    <p><span className="text-slate-500">Account SID:</span> <code className="text-slate-700">{twilio.account_sid_masked}</code></p>
                    <p><span className="text-slate-500">Auth Token:</span> <code className="text-slate-700">{twilio.auth_token_masked}</code></p>
                    <p><span className="text-slate-500">SMS From:</span> <code className="text-slate-700">{twilio.sms_from || '(not set)'}</code></p>
                    <p><span className="text-slate-500">WhatsApp From:</span> <code className="text-slate-700">{twilio.whatsapp_from || '(not set)'}</code></p>
                    {twilio.updated_at && <p className="text-xs text-slate-400">Last updated: {new Date(twilio.updated_at).toLocaleString('en-IN')}</p>}
                  </div>
                )}
                <form onSubmit={saveTwilio} className="space-y-3">
                  <div>
                    <Label>Account SID</Label>
                    <Input value={twilioForm.account_sid} onChange={(e) => setTwilioForm({...twilioForm, account_sid: e.target.value})} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-sid-input" />
                  </div>
                  <div>
                    <Label>Auth Token</Label>
                    <Input type="password" value={twilioForm.auth_token} onChange={(e) => setTwilioForm({...twilioForm, auth_token: e.target.value})} placeholder="Leave blank to keep existing" data-testid="twilio-token-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>SMS Sender Number</Label>
                      <Input value={twilioForm.sms_from} onChange={(e) => setTwilioForm({...twilioForm, sms_from: e.target.value})} placeholder="+15005550006" data-testid="twilio-sms-from-input" />
                    </div>
                    <div>
                      <Label>WhatsApp Sender</Label>
                      <Input value={twilioForm.whatsapp_from} onChange={(e) => setTwilioForm({...twilioForm, whatsapp_from: e.target.value})} placeholder="whatsapp:+14155238886" data-testid="twilio-whatsapp-from-input" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-blue-800 underline">console.twilio.com</a>. Leave any field blank to keep the existing value.
                  </p>
                  <Button type="submit" disabled={twilioSaving} className="bg-blue-800 hover:bg-blue-900" data-testid="twilio-save-button">
                    {twilioSaving ? 'Saving...' : (twilio?.configured ? 'Update Credentials' : 'Connect Twilio')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }} className="text-lg">Inbound Webhook</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Paste this URL into Twilio Console under Messaging → Services → Sender Pool → Integration → "A message comes in".</p>
              </CardHeader>
              <CardContent>
                <div className="p-3 bg-slate-900 text-slate-100 rounded-lg font-mono text-xs break-all">
                  {twilio?.webhook_url || '—'}
                </div>
                <Button
                  onClick={copyWebhook}
                  variant="outline"
                  className="mt-3 w-full"
                  data-testid="copy-webhook-button"
                >
                  {webhookCopied ? <><Check className="h-4 w-4 mr-2" /> Copied!</> : <><Copy className="h-4 w-4 mr-2" /> Copy Webhook URL</>}
                </Button>
                <div className="mt-4 space-y-2 text-xs text-slate-600">
                  <p><strong className="text-slate-900">HTTP Method:</strong> POST</p>
                  <p><strong className="text-slate-900">Auth:</strong> Twilio signature (auto-validated when creds are set)</p>
                  <p className="pt-2 border-t border-slate-200">Once configured, every SMS or WhatsApp reply from a client lands on their conversation thread automatically.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="commissions" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}><DollarSign className="inline h-5 w-5 mr-2" />Commission Rates</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Lookup order (most specific first): <strong>Insurer + Product + Stage</strong> → <strong>Insurer + Product</strong> → <strong>Product default</strong>. Anything you don't override still calculates via the seeded product-type default.
                </p>
              </div>
              <Dialog open={addRateOpen} onOpenChange={setAddRateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-rate-override-button"><Plus className="h-4 w-4 mr-1.5" /> Add Override</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add commission rate override</DialogTitle>
                    <DialogDescription>Insurer- or stage-specific rate. Leave insurer blank for a product-type default; pick "Both" to apply to New Business + Renewal.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={addRate} className="space-y-3">
                    <div>
                      <Label>Product Type *</Label>
                      <Select value={rateForm.product_type} onValueChange={(v) => setRateForm({...rateForm, product_type: v})}>
                        <SelectTrigger data-testid="rate-form-product-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Term','Endowment','ULIP','Whole Life','Health','Motor','Home','Travel','Personal Accident','Commercial','Mutual Fund','PPF','Fixed Deposit','Recurring Deposit','Post Office','Loan'].map(pt => <SelectItem key={pt} value={pt}>{pt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={rateForm.category} onValueChange={(v) => setRateForm({...rateForm, category: v})}>
                        <SelectTrigger data-testid="rate-form-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="life_insurance">Life Insurance</SelectItem>
                          <SelectItem value="general_insurance">General Insurance</SelectItem>
                          <SelectItem value="investment">Investment</SelectItem>
                          <SelectItem value="loans">Loans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Insurer (blank = applies to all insurers)</Label>
                      {availableInsurers.length > 0 ? (
                        <Select value={rateForm.insurer || '_none'} onValueChange={(v) => setRateForm({...rateForm, insurer: v === '_none' ? '' : v})}>
                          <SelectTrigger data-testid="rate-form-insurer"><SelectValue placeholder="— All insurers —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— All insurers (fallback) —</SelectItem>
                            {availableInsurers.map(i => <SelectItem key={i.insurer_id || i.name} value={i.name}>{i.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={rateForm.insurer} onChange={(e) => setRateForm({...rateForm, insurer: e.target.value})} placeholder="e.g. Star Health" data-testid="rate-form-insurer-input" />
                      )}
                    </div>
                    <div>
                      <Label>Product Name <span className="text-slate-400 font-normal text-xs">(optional — overrides the type-level rate for this specific product)</span></Label>
                      <Input value={rateForm.product_name} onChange={(e) => setRateForm({...rateForm, product_name: e.target.value})} placeholder="e.g. Family Health Optima" data-testid="rate-form-product-name" />
                    </div>
                    <div>
                      <Label>Policy Stage</Label>
                      <Select value={rateForm.policy_stage} onValueChange={(v) => setRateForm({...rateForm, policy_stage: v})}>
                        <SelectTrigger data-testid="rate-form-stage"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Both">Both (New Business + Renewal)</SelectItem>
                          <SelectItem value="New Business">New Business only</SelectItem>
                          <SelectItem value="Renewal">Renewal only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rate % *</Label>
                      <Input type="number" step="0.1" min="0" value={rateForm.rate_percent} onChange={(e) => setRateForm({...rateForm, rate_percent: e.target.value})} placeholder="e.g. 35" required data-testid="rate-form-percent" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setAddRateOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="rate-form-submit">Add Override</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {commissionRates.length === 0 ? (
                <p className="text-center text-slate-500 py-6" data-testid="commission-rates-empty">Loading rates…</p>
              ) : (
                <Table data-testid="commission-rates-table">
                  <TableHeader><TableRow>
                    <TableHead>Product Type</TableHead>
                    <TableHead>Product Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Insurer</TableHead>
                    <TableHead>Policy Stage</TableHead>
                    <TableHead className="text-right">Rate %</TableHead>
                    <TableHead className="text-right w-40">Action</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {commissionRates.map((r) => {
                      const isOverride = Boolean(r.insurer);
                      return (
                        <TableRow key={r.rate_id} data-testid={`rate-row-${r.rate_id}`} className={isOverride ? 'bg-blue-50/40' : ''}>
                          <TableCell className="font-medium">{r.product_type}</TableCell>
                          <TableCell className={r.product_name ? 'text-slate-900' : 'text-xs text-slate-400 italic'}>{r.product_name || '—'}</TableCell>
                          <TableCell><Badge className="bg-slate-100 text-slate-800 capitalize">{(r.category || '').replace(/_/g, ' ')}</Badge></TableCell>
                          <TableCell className={isOverride ? 'text-slate-900 font-medium' : 'text-xs text-slate-400 italic'}>{r.insurer || '— all insurers —'}</TableCell>
                          <TableCell>
                            <Badge className={
                              r.policy_stage === 'New Business' ? 'bg-emerald-100 text-emerald-800' :
                              r.policy_stage === 'Renewal'      ? 'bg-purple-100 text-purple-800' :
                              'bg-slate-100 text-slate-800'
                            }>{r.policy_stage || 'Both'}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {editingRateId === r.rate_id ? (
                              <Input type="number" step="0.1" min="0" value={editingRateValue} onChange={(e) => setEditingRateValue(e.target.value)} className="w-20 text-right ml-auto" data-testid={`rate-input-${r.rate_id}`} />
                            ) : (
                              <span className="font-semibold">{r.rate_percent}%</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {editingRateId === r.rate_id ? (
                              <>
                                <Button size="sm" onClick={() => saveRate(r)} className="bg-blue-800 hover:bg-blue-900 h-7 px-2 mr-1" data-testid={`rate-save-${r.rate_id}`}>Save</Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditingRateId(''); setEditingRateValue(''); }}>Cancel</Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="outline" onClick={() => { setEditingRateId(r.rate_id); setEditingRateValue(String(r.rate_percent)); }} data-testid={`rate-edit-${r.rate_id}`}>
                                  <Edit className="h-3 w-3 mr-1" /> Edit
                                </Button>
                                {isOverride && (
                                  <Button size="sm" variant="ghost" onClick={() => deleteRate(r)} data-testid={`rate-delete-${r.rate_id}`} title="Delete this override">
                                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                  </Button>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Commission Annexures — insurer rate-schedule PDFs */}
          <Card className="border-slate-200 mt-6" data-testid="commission-annexures-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}><Package className="inline h-5 w-5 mr-2" />Rate Schedule Annexures</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Upload the PDFs insurers send you — global per insurer, or scoped to a single product type. Keep them handy when a client asks "where did that rate come from?"</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="annexure-upload-row">
                <div>
                  <Label className="text-xs">Insurer *</Label>
                  {availableInsurers.length > 0 ? (
                    <Select value={annexureForm.insurer || '_none'} onValueChange={(v) => setAnnexureForm({ ...annexureForm, insurer: v === '_none' ? '' : v })}>
                      <SelectTrigger data-testid="annexure-insurer-select"><SelectValue placeholder="Pick insurer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Pick insurer —</SelectItem>
                        {availableInsurers.map(i => <SelectItem key={i.insurer_id || i.name} value={i.name}>{i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={annexureForm.insurer} onChange={(e) => setAnnexureForm({ ...annexureForm, insurer: e.target.value })} placeholder="e.g. HDFC Life" data-testid="annexure-insurer-input" />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Product Type <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Select value={annexureForm.product_type || '_all'} onValueChange={(v) => setAnnexureForm({ ...annexureForm, product_type: v === '_all' ? '' : v })}>
                    <SelectTrigger data-testid="annexure-product-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">— All product types —</SelectItem>
                      {['Term','Endowment','ULIP','Whole Life','Health','Motor','Home','Travel','Personal Accident','Commercial'].map(pt => <SelectItem key={pt} value={pt}>{pt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Label <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input value={annexureForm.label} onChange={(e) => setAnnexureForm({ ...annexureForm, label: e.target.value })} placeholder="e.g. FY26 Q1 rates" data-testid="annexure-label-input" />
                </div>
                <div>
                  <Label className="text-xs">File (PDF/Image) *</Label>
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setAnnexureForm({ ...annexureForm, file: e.target.files?.[0] || null })} data-testid="annexure-file-input" />
                </div>
                <div className="flex items-end">
                  <Button onClick={uploadAnnexure} disabled={annexureUploading || !annexureForm.file || !annexureForm.insurer} className="bg-blue-800 hover:bg-blue-900 w-full" data-testid="annexure-upload-button">
                    {annexureUploading ? 'Uploading…' : 'Upload'}
                  </Button>
                </div>
              </div>

              {annexures.length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm" data-testid="annexures-empty">No annexures uploaded yet.</p>
              ) : (
                <Table data-testid="annexures-table">
                  <TableHeader><TableRow>
                    <TableHead>Insurer</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {annexures.map((a) => (
                      <TableRow key={a.annexure_id} data-testid={`annexure-row-${a.annexure_id}`}>
                        <TableCell className="font-medium">{a.insurer}</TableCell>
                        <TableCell>
                          {a.product_type
                            ? <Badge className="bg-indigo-100 text-indigo-800">{a.product_type}</Badge>
                            : <Badge className="bg-slate-100 text-slate-700">All product types</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">{a.label || a.filename}</TableCell>
                        <TableCell className="text-xs text-slate-500">{a.uploaded_at ? new Date(a.uploaded_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => downloadAnnexure(a)} data-testid={`annexure-download-${a.annexure_id}`}>
                            <Package className="h-3 w-3 mr-1" /> Download
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteAnnexure(a.annexure_id)} data-testid={`annexure-delete-${a.annexure_id}`} title="Delete annexure">
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="data" className="mt-6 space-y-6">
          {/* Import History */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}><History className="inline h-5 w-5 mr-2" />Import History</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Every past bulk upload. Rollback a batch to permanently delete the records it inserted.</p>
            </CardHeader>
            <CardContent>
              {importsLoading ? (
                <p className="text-center text-slate-500 py-8">Loading…</p>
              ) : imports.length === 0 ? (
                <p className="text-center text-slate-500 py-8" data-testid="imports-empty">No bulk uploads yet. Start with the "Bulk Upload" option from Clients or Products.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead className="text-right">Inserted</TableHead>
                      <TableHead className="text-right">Skipped</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {imports.map(b => {
                      const rolled = Boolean(b.rolled_back_at);
                      const totalRolled = rolled ? Object.values(b.rolled_back_counts || {}).reduce((a, x) => a + x, 0) : 0;
                      return (
                        <TableRow key={b.batch_id} data-testid={`import-row-${b.batch_id}`}>
                          <TableCell className="text-sm">{new Date(b.created_at).toLocaleString('en-IN')}</TableCell>
                          <TableCell><Badge className="bg-slate-100 text-slate-800 capitalize">{b.kind}</Badge></TableCell>
                          <TableCell className="text-sm text-slate-700 max-w-xs truncate" title={b.filename}>{b.filename}</TableCell>
                          <TableCell className="text-right font-medium text-emerald-700">{b.inserted}</TableCell>
                          <TableCell className="text-right text-slate-500">{b.skipped || 0}</TableCell>
                          <TableCell>
                            {rolled
                              ? <Badge className="bg-red-100 text-red-800" data-testid={`import-rolled-${b.batch_id}`}>Rolled back · {totalRolled}</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={rolled || rollbackBusyId === b.batch_id}
                              onClick={() => rollbackBatch(b)}
                              className="border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40"
                              data-testid={`rollback-batch-${b.batch_id}`}
                            >
                              <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                              {rollbackBusyId === b.batch_id ? 'Rolling back…' : (rolled ? 'Already reverted' : 'Rollback This Batch')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Danger zone: Clear All My Data */}
          <Card className="border-red-200 bg-red-50/40">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="text-red-900"><ShieldAlert className="inline h-5 w-5 mr-2 text-red-700" />Clear All My Data</CardTitle>
              <p className="text-sm text-red-800 mt-1">Permanently deletes every client, product/policy, lead, task, quotation, and message from your account. This cannot be undone.</p>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-red-200 bg-white p-4 mb-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">What will be deleted right now</p>
                {dataSummary.total === 0 ? (
                  <p className="text-sm text-slate-600" data-testid="purge-summary-empty">Nothing to delete — your account is already empty.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="purge-summary">
                    {Object.entries(dataSummary.counts).map(([k, n]) => (
                      <span key={k} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs">
                        <strong className="text-slate-900">{n}</strong>
                        <span className="text-slate-600 capitalize">{k.replace(/_/g, ' ')}</span>
                      </span>
                    ))}
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs text-white">
                      <strong>{dataSummary.total} records total</strong>
                    </span>
                  </div>
                )}
              </div>
              <Dialog open={purgeOpen} onOpenChange={(o) => { setPurgeOpen(o); if (!o) setPurgeConfirm(''); }}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={dataSummary.total === 0}
                    className="border-red-500 text-red-700 hover:bg-red-100 hover:text-red-800 disabled:opacity-40"
                    data-testid="open-purge-dialog"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Clear All My Data…
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-red-800">This will permanently delete {dataSummary.total} records</DialogTitle>
                    <DialogDescription>This action cannot be undone. Every deletion is written to the audit log.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    <div className="rounded-md border border-red-200 bg-red-50 p-3">
                      <p className="font-medium text-red-900 mb-2">The following will be irreversibly deleted:</p>
                      <ul className="space-y-1 text-red-800">
                        {Object.entries(dataSummary.counts).map(([k, n]) => (
                          <li key={k}>• <strong>{n}</strong> {k.replace(/_/g, ' ')}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <Label htmlFor="purge-confirm">Type <code className="bg-slate-100 px-1.5 py-0.5 rounded text-red-700 font-bold">DELETE ALL</code> to confirm</Label>
                      <Input
                        id="purge-confirm"
                        value={purgeConfirm}
                        onChange={(e) => setPurgeConfirm(e.target.value)}
                        placeholder="DELETE ALL"
                        autoComplete="off"
                        data-testid="purge-confirm-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button variant="outline" onClick={() => setPurgeOpen(false)} data-testid="purge-cancel">Cancel</Button>
                      <Button
                        onClick={purgeAll}
                        disabled={purgeConfirm !== 'DELETE ALL' || purgeBusy}
                        className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                        data-testid="purge-confirm-button"
                      >
                        {purgeBusy ? 'Deleting…' : 'Delete everything'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Audit log */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}><Database className="inline h-5 w-5 mr-2" />Deletion Audit Log</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Every rollback and data wipe is recorded here.</p>
            </CardHeader>
            <CardContent>
              {auditLog.length === 0 ? (
                <p className="text-center text-slate-500 py-6" data-testid="audit-empty">No destructive actions logged yet.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead className="text-right">Records</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {auditLog.map((row, i) => (
                      <TableRow key={i} data-testid={`audit-row-${i}`}>
                        <TableCell className="text-xs text-slate-600">{new Date(row.created_at).toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-sm">{row.user_name || row.user_email}</TableCell>
                        <TableCell>
                          <Badge className={row.action === 'purge_all_data' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                            {row.action.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{row.details?.deleted_total ?? 0}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-xs truncate" title={row.details?.filename || JSON.stringify(row.details?.deleted_counts || {})}>
                          {row.details?.filename || Object.entries(row.details?.deleted_counts || {}).map(([k, n]) => `${n} ${k}`).join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
